/**
 * `useEventJoinLinks` - the Map lead's shared Telegram join link (#2658).
 *
 *   * `GET  /api/teams/{tid}/join-links`                → the team's links, newest first
 *   * `POST /api/teams/{tid}/join-links` `{label}`       → make a link
 *   * `POST /api/teams/{tid}/join-links/{id}/revoke`     → stop a link
 *
 * Lead or co-lead only; Map teams only. A member's read answers 403, which the
 * card treats as "show nothing".
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";

export type EventJoinLink = components["schemas"]["JoinLinkOut"];

export const EVENT_JOIN_LINKS_KEY = (teamId: string): readonly string[] => [
  "team",
  teamId,
  "join-links",
];

export function useEventJoinLinks(teamId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: EVENT_JOIN_LINKS_KEY(teamId ?? ""),
    enabled: Boolean(teamId) && enabled,
    retry: false,
    queryFn: () => apiFetch<EventJoinLink[]>(`/api/teams/${teamId}/join-links`),
  });
}

export function useCreateEventJoinLink(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation<EventJoinLink, Error, string>({
    mutationFn: (label) =>
      apiFetch<EventJoinLink>(`/api/teams/${teamId}/join-links`, {
        method: "POST",
        body: JSON.stringify({ label }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EVENT_JOIN_LINKS_KEY(teamId) });
    },
  });
}

export function useRevokeEventJoinLink(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation<EventJoinLink, Error, string>({
    mutationFn: (linkId) =>
      apiFetch<EventJoinLink>(`/api/teams/${teamId}/join-links/${linkId}/revoke`, {
        method: "POST",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EVENT_JOIN_LINKS_KEY(teamId) });
    },
  });
}
